import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

export type CommunityEvent = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  venue: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  organiser: string | null;
  website_url: string | null;
  ticket_url: string | null;
  rsvp_url: string | null;
  maps_url: string | null;
  image_url: string | null;
  category: string;
  event_type: string;
  is_recurring: boolean;
  recurrence_pattern: string | null;
  is_featured: boolean;
  status: "draft" | "published" | "archived";
  display_order: number;
  created_at: string;
  updated_at: string;
};

export const EVENT_CATEGORY_LABELS: Record<string, string> = {
  card_show: "Card Show",
  trade_night: "Trade Night",
  tournament: "Tournament",
  grading_submission: "Grading Submission Event",
  collector_meetup: "Collector Meetup",
  auction_event: "Auction Event",
  convention: "Convention",
  partner_event: "Partner Event",
  local_store: "Local Store Event",
};

export const EVENT_TYPE_LABELS: Record<string, string> = {
  card_show: "Card Show",
  tournament: "Tournament",
  convention: "Convention",
  local_store: "Local Store Event",
  meetup: "Meetup",
  other: "Other",
};

export const RECURRENCE_LABELS: Record<string, string> = {
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};


export async function fetchUpcomingEvents(limit = 12): Promise<CommunityEvent[]> {
  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from("community_events")
    .select("*")
    .gte("start_date", today)
    .order("is_featured", { ascending: false })
    .order("start_date", { ascending: true })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as CommunityEvent[];
}

export async function fetchEventBySlug(slug: string): Promise<CommunityEvent | null> {
  const { data, error } = await supabase.from("community_events").select("*").eq("slug", slug).maybeSingle();
  if (error) return null;
  return (data as CommunityEvent) ?? null;
}

const EventInput = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(2).max(200),
  slug: z.string().trim().min(2).max(200).regex(/^[a-z0-9-]+$/i),
  description: z.string().trim().max(20000).optional().nullable(),
  start_date: z.string().min(1),
  end_date: z.string().optional().nullable().or(z.literal("")),
  start_time: z.string().optional().nullable().or(z.literal("")),
  end_time: z.string().optional().nullable().or(z.literal("")),
  venue: z.string().trim().max(200).optional().nullable(),
  address: z.string().trim().max(300).optional().nullable(),
  city: z.string().trim().max(120).optional().nullable(),
  country: z.string().trim().max(80).optional().nullable(),
  organiser: z.string().trim().max(200).optional().nullable(),
  website_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  ticket_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  rsvp_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  maps_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  image_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  category: z.string().trim().min(1).max(60),
  event_type: z.string().trim().min(1).max(40).default("other"),
  is_recurring: z.boolean().default(false),
  recurrence_pattern: z.string().trim().max(40).optional().nullable().or(z.literal("")),
  is_featured: z.boolean().default(false),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  display_order: z.number().int().min(0).max(10000).default(0),
});


export const listAllEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("community_events")
      .select("*")
      .order("start_date", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as CommunityEvent[];
  });

export const upsertEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => EventInput.parse(d))
  .handler(async ({ data, context }) => {
    const cleanUrl = (v?: string | null) => (v ? v : null);
    const payload = {
      ...data,
      end_date: data.end_date || null,
      start_time: data.start_time || null,
      end_time: data.end_time || null,
      website_url: cleanUrl(data.website_url),
      ticket_url: cleanUrl(data.ticket_url),
      rsvp_url: cleanUrl(data.rsvp_url),
      maps_url: cleanUrl(data.maps_url),
      image_url: cleanUrl(data.image_url),
      recurrence_pattern: data.recurrence_pattern || null,
      created_by: context.userId,
    };

    const { id, ...rest } = payload;
    if (id) {
      const { error } = await context.supabase.from("community_events").update(rest as never).eq("id", id);
      if (error) throw new Error(error.message);
      return { ok: true, id };
    }
    const { data: row, error } = await context.supabase
      .from("community_events")
      .insert(rest as never)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id };
  });

export const deleteEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("community_events").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
